import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Alert, Box, CircularProgress, Divider, Stack, Typography, useTheme } from "@mui/material";
import { CheckCircle, Gift } from "lucide-react";

import PagePublic from "../../components/Page/PagePublic";
import { Linkgroup } from "../../components/Header/Linkgroup";
import ButtonForm from "../../components/Button/ButtonForm";
import ButtonsMain from "../../components/Button/ButtonMain";
import { useTranslation } from "../../language";
import { getGiftPreview, redeemGift } from "../auth";
import { classifyRedeemError } from "./redeemErrors";

const PENDING_KEY = "pendingGiftRedemption";
const BLOCKED_KINDS = ["already_redeemed", "not_payable", "expired"];
const STEP_KEYS = ["gift_redeem_step_1", "gift_redeem_step_2", "gift_redeem_step_3"];

function NumberedItem({ number, text, accentColor, isLast }) {
  const theme = useTheme();
  return (
    <>
      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 1.5 }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 28, height: 28,
            borderRadius: '50%',
            border: `2px solid ${accentColor}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mt: '2px',
          }}
        >
          <Typography
            variant="number4"
            sx={{ color: accentColor, fontWeight: 700, fontSize: '0.75rem', lineHeight: 1 }}
          >
            {number}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          {text}
        </Typography>
      </Stack>
      {!isLast && (
        <Divider sx={{ borderColor: theme.palette.mode === 'light' ? 'grey.C900' : 'greydark.C400' }} />
      )}
    </>
  );
}

export default function RedeemGiftPage() {
  const { code } = useParams();
  const { t, language, changeLanguage } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [preview, setPreview] = useState({ status: "loading", gift: null });
  const [redeem, setRedeem] = useState({ status: "idle" });

  useEffect(() => { changeLanguage("de"); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    setPreview({ status: "loading", gift: null });
    getGiftPreview(code)
      .then((gift) => { if (!cancelled) setPreview({ status: "ok", gift }); })
      .catch((e) => {
        if (!cancelled) setPreview({ status: e.response?.status === 404 ? "not_found" : "error", gift: null });
      });
    return () => { cancelled = true; };
  }, [code]);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(language, { year: "numeric", month: "long", day: "numeric" });

  const handleRedeem = async () => {
    setRedeem({ status: "submitting" });
    try {
      const result = await redeemGift(code);
      try { localStorage.removeItem(PENDING_KEY); } catch { /* storage unavailable */ }
      setRedeem({ status: "success", result });
    } catch (e) {
      const { kind, email } = classifyRedeemError(e);
      if (kind === "auth_required") {
        try { localStorage.setItem(PENDING_KEY, JSON.stringify({ code, email })); } catch { /* storage unavailable */ }
      }
      setRedeem({ status: kind, email });
    }
  };

  const accentColor = theme.palette.tertiary.C300;
  const cardSx = {
    border: "1px solid",
    borderRadius: 4,
    overflow: "hidden",
    p: { xs: 2.5, sm: 4 },
    ...(theme.palette.mode === "light"
      ? { bgcolor: "grey.C50", borderColor: "grey.C900" }
      : { bgcolor: "greydark.C980", borderColor: "greydark.C400",
          backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.6)" }),
    borderLeft: `10px solid ${accentColor}`,
  };

  const renderBody = () => {
    if (preview.status === "loading") {
      return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>;
    }
    if (preview.status === "not_found") {
      return <Box sx={cardSx}><Alert severity="error">{t("gift_redeem_not_found")}</Alert></Box>;
    }
    if (preview.status === "error") {
      return <Box sx={cardSx}><Alert severity="error">{t("gift_redeem_load_error")}</Alert></Box>;
    }

    if (redeem.status === "success") {
      return (
        <Box sx={cardSx}>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <CheckCircle size={40} color={theme.palette.traffic.green} />
            <Typography variant="h6" fontWeight={700}>{t("gift_redeem_success_title")}</Typography>
            <Typography variant="body1" color="text.secondary">
              {t("gift_redeem_success_text")} {formatDate(redeem.result.expires_at)}
            </Typography>
            <ButtonsMain name={t("gift_redeem_continue")} action={() => navigate("/Welcome")} sx={{ px: "24px" }} />
          </Stack>
        </Box>
      );
    }

    const { gift } = preview;
    const expiredByDate = gift.redemption_expires_at && new Date(gift.redemption_expires_at) < new Date();
    const blockedKind = BLOCKED_KINDS.includes(redeem.status)
      ? redeem.status
      : gift.status === "redeemed" ? "already_redeemed"
      : expiredByDate ? "expired"
      : null;

    return (
      <Box sx={cardSx}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{
              width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
              bgcolor: theme.palette.mode === "light" ? "grey.C100" : "greydark.C900",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Gift size={20} color={accentColor} />
            </Box>
            <Typography variant="h6" fontWeight={700}>{t("gift_redeem_headline")}</Typography>
          </Stack>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
              {t("gift_redeem_from")}
            </Typography>
            <Typography variant="body1" fontWeight={600}>{gift.sender_name}</Typography>
          </Box>

          {gift.recipient_name && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                {t("gift_redeem_for")}
              </Typography>
              <Typography variant="body1" fontWeight={600}>{gift.recipient_name}</Typography>
            </Box>
          )}

          {gift.message && (
            <Typography variant="body1" color="text.secondary" sx={{ fontStyle: "italic", whiteSpace: "pre-line" }}>
              “{gift.message}”
            </Typography>
          )}

          {gift.redemption_expires_at && (
            <Typography variant="caption" fontWeight={600}>
              {t("gift_redeem_valid_until")} {formatDate(gift.redemption_expires_at)}
            </Typography>
          )}

          {blockedKind && <Alert severity="warning">{t(`gift_redeem_${blockedKind}`)}</Alert>}
          {redeem.status === "error" && <Alert severity="error">{t("gift_redeem_error")}</Alert>}

          {redeem.status === "auth_required" && (
            <Stack spacing={1}>
              <Typography variant="subtitle1" fontWeight={700}>{t("gift_redeem_auth_title")}</Typography>
              <Box>
                {STEP_KEYS.map((key, i) => (
                  <NumberedItem
                    key={key}
                    number={i + 1}
                    text={t(key)}
                    accentColor={accentColor}
                    isLast={i === STEP_KEYS.length - 1}
                  />
                ))}
              </Box>
              <Typography variant="body2" color="text.secondary">{t("gift_redeem_auth_closing")}</Typography>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <ButtonsMain
                  name={t("gift_redeem_register")}
                  action={() => navigate("/RegisterPage2Col", { state: { prefillEmail: redeem.email } })}
                  sx={{ px: "24px" }}
                />
              </Box>
            </Stack>
          )}

          {!blockedKind && redeem.status !== "auth_required" && (
            <ButtonForm
              name={redeem.status === "submitting" ? <CircularProgress size={18} color="inherit" /> : t("gift_redeem_button")}
              onClick={handleRedeem}
              disabled={redeem.status === "submitting"}
            />
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <PagePublic
      linkgroup={<Linkgroup destination="/" button_name="HOME" />}
      main={
        <Box sx={{ width: "100%", maxWidth: 640, mx: "auto", px: { xs: 2, sm: 4 }, pt: { xs: 4, sm: 6 }, pb: 6 }}>
          {renderBody()}
        </Box>
      }
    />
  );
}
